import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { TransactionQueryKey } from "@/config/constants/QueryKeys.tsx";
import { Transaction } from "@/types/Transaction.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { TransactionStatus } from "@/types/Status.tsx";
import { isSNS } from "@/utils/utils";
import bgSNS from "../assets/SVG/bgSNS.svg";
import loading from "../assets/SVG/SNS_loading.json";
import { Player } from "@lottiefiles/react-lottie-player";

const WaitingApproval: React.FC = () => {
  const location = useLocation();
  const state = location.state as { transaction_id: string };
  const navigate = useNavigate();

  const { data: transaction, isSuccess } = useQuery<ApiResponse<Transaction>>({
    queryFn: async () =>
      GenericApi.get<Transaction>(
        `${ApiRoutes.TRANSACTIONS}?obj_id=${state?.transaction_id}`
      ),
    queryKey: [TransactionQueryKey, state?.transaction_id],
    refetchInterval: 2000,
  });

  useEffect(() => {
    if (
      isSuccess &&
      transaction?.data?.status === TransactionStatus.COMPLETED
    ) {
      navigate(RoutesPathList.TransactionApproved, {
        state: {
          transaction_id: transaction?.data?.id,
          isComingFromTransactionApproved: true,
        },
      });
    } else if (
      isSuccess &&
      transaction?.data?.status === TransactionStatus.DECLINED
    ) {
      navigate(RoutesPathList.ScanIDPOS);
    }
  }, [isSuccess, navigate, transaction?.data?.status]);

  return !isSNS() ? (
    <div className="flex items-center flex-col gap-10 justify-center h-screen">
      <svg
        className="approval-loader"
        width="83"
        height="83"
        viewBox="0 0 83 83"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M0 42.4802V40.8594L0.511693 39.6691C1.18745 38.4332 2.3083 37.8762 3.6517 37.6212L19.2185 37.6098C24.0853 38.1539 24.3404 44.6644 19.5434 45.5657L3.32682 45.556C2.35054 45.2929 1.33365 44.7716 0.734239 43.9336C0.42235 43.4984 0.315138 42.9089 0 42.4785V42.4802Z"
          fill="#202A43"
        />
        <path
          d="M42.1521 83H40.8559C40.5423 82.7337 40.0794 82.6785 39.7253 82.4284C38.9423 81.8746 38.5703 80.9099 38.4273 79.9973L38.4176 62.9747C38.7636 59.1292 43.9455 58.8839 44.592 62.6499L44.5823 80.3204C44.3825 81.1292 43.978 81.9363 43.2844 82.4251C42.9302 82.6752 42.4689 82.7304 42.1538 82.9967L42.1521 83Z"
          fill="#202A43"
        />
        <path
          d="M40.8088 0.0488891C43.4663 -0.329497 45.8152 1.53158 46.2116 4.14131L46.1742 19.1907C45.7909 20.7351 44.6976 22.1334 43.1707 22.6709C40.0843 23.7557 37.1928 21.7615 36.7964 18.5801C37.0921 14.0037 36.4261 8.98888 36.7964 4.46448C36.9686 2.35169 38.6661 0.354197 40.8104 0.0488891H40.8088Z"
          fill="#202A43"
        />
        <path
          d="M21.9996 5.07023C23.4453 4.82338 25.2533 5.34305 26.2718 6.41001C27.0905 7.26909 28.4566 9.96164 29.1584 11.1423C31.0411 14.309 32.9969 17.5083 34.5304 20.8455C35.0339 24.4897 31.6616 27.244 28.2081 25.8701C26.686 25.2644 25.968 23.7671 25.1542 22.4387C22.9498 18.8367 20.9632 15.0869 18.7735 11.4736C17.4934 8.95803 19.2218 5.5428 21.9979 5.07023H21.9996Z"
          fill="#202A43"
        />
        <path
          d="M8.86935 18.6889C9.66044 18.5833 10.7456 18.9098 11.4603 19.2719C15.8706 21.4967 20.1802 24.5774 24.5645 26.9176C28.0197 30.0405 25.2809 35.3233 20.6968 34.3505C20.3946 34.2872 19.4281 33.7318 19.074 33.5418C16.4765 32.1435 13.9505 30.5472 11.4034 29.0531C9.98208 28.22 6.73161 26.7373 5.85767 25.521C3.90836 22.8122 5.59614 19.1257 8.86935 18.6889Z"
          fill="#202A43"
        />
        <path
          d="M21.6747 48.9939C24.3842 48.5343 26.5285 50.6829 26.0704 53.3884C26.0168 53.7002 25.7179 54.366 25.5294 54.6307C24.7578 55.7204 21.92 57.1171 20.6448 57.8511C17.1377 59.8697 13.5704 61.7909 10.0568 63.7981C6.18905 65.0794 3.44215 60.3358 6.40023 57.549L20.5343 49.473C20.8657 49.309 21.3206 49.0524 21.6747 48.9923V48.9939Z"
          fill="#202A43"
        />
        <path
          d="M30.1087 57.1024C32.6883 56.7403 34.6587 59.1828 33.8091 61.6122C31.5544 65.4302 29.4605 69.3489 27.2123 73.1701C26.5739 74.2549 25.3378 76.6827 24.3777 77.3047C22.5389 78.4951 20.0568 77.6116 19.3664 75.5524C19.1211 74.8217 19.1649 74.1623 19.3664 73.4283L27.9709 58.3675C28.5232 57.739 29.2607 57.221 30.1087 57.1008V57.1024Z"
          fill="#202A43"
        />
        <path
          d="M51.668 57.2616C52.6654 57.083 53.7684 57.5945 54.3857 58.3708C57.0595 63.3012 60.1751 68.0334 62.7742 73.0012C63.1251 73.6702 63.6287 74.5277 63.5507 75.2975C63.3119 77.6409 60.4789 78.6348 58.7732 77.001L49.9039 61.5554C48.796 59.9899 49.8178 57.5929 51.668 57.26V57.2616Z"
          fill="#202A43"
        />
        <path
          d="M77.1373 20.3096C77.2332 20.4054 77.7611 21.3717 77.7936 21.5179C78.008 22.4906 77.5418 23.5771 76.7442 24.1438C71.6387 26.9127 66.754 30.1055 61.6631 32.8938C61.3528 33.0627 60.7762 33.4005 60.4805 33.4947C57.7888 34.3375 55.9581 30.7356 58.4679 29.0515L74.3011 19.9199C75.1929 19.5626 75.5747 19.5106 76.4762 19.9166C76.5623 19.9556 77.1049 20.2771 77.1373 20.3096Z"
          fill="#202A43"
        />
        <path
          d="M76.9765 59.2087C77.1357 59.6651 77.5906 59.8827 77.6588 60.391C77.792 61.3767 77.6279 62.4664 76.7442 63.0429C75.3586 63.9475 74.5155 63.4083 73.2257 62.7165C68.0666 59.9477 63.0731 56.5925 57.9139 53.7928C57.1667 52.8476 56.897 51.6313 57.641 50.5887C58.2534 49.7312 59.3499 49.4162 60.3668 49.6386C60.9126 49.7588 62.9513 51.045 63.6108 51.4201C68.0893 53.9714 72.4915 56.6721 76.9765 59.2087Z"
          fill="#202A43"
        />
        <path
          d="M62.2089 39.1104L80.6526 39.0795C83.4612 39.3946 83.9014 43.1476 81.1432 43.9385H61.8499C59.142 43.1346 59.5042 39.5602 62.2089 39.1104Z"
          fill="#202A43"
        />
        <path
          d="M60.7502 5.39664C62.6329 5.15142 63.8561 7.02062 63.3184 8.77614L53.9 24.9688C51.9328 27.0572 48.7571 25.0273 49.8064 22.3347L58.7148 6.92805C59.1631 6.14205 59.8275 5.51682 60.7502 5.39664Z"
          fill="#202A43"
        />
      </svg>

      <div className="text-center">
        <h1 className="text-[1.6875rem] text-[#222B45] font-[700] font-satoshi mb-4">
          Waiting for Approval
        </h1>
      </div>
    </div>
  ) : (
    <div
      className="overflow-hidden pt-[15rem] min-h-screen bg-[#1C1C1C] pb-[6rem] flex flex-col items-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Player
        autoplay={true}
        loop={true}
        src={loading}
        style={{
          height: "6rem",
          width: "6rem",
          transform: "rotate(90deg)",
        }}
      />
      <div className="text-center">
        <h1 className="text-[1.6875rem] text-white font-[helvetica] font-[300] mb-4">
          Waiting for Approval
        </h1>
      </div>
    </div>
  );
};

export default WaitingApproval;
